import { toaster } from '@/components/ui/toaster';
import type { BatchAttachmentInfo } from '@fluenceur/types';
import type { AxiosError } from 'axios';
import { useCallback, useRef, useState, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteBulkEmailAttachment } from './actions/use-delete-bulk-email-attachment';
import { useUploadBulkEmailAttachment } from './actions/use-upload-bulk-email-attachment';
import {
  attachmentSetError,
  attachmentTotalLimit,
  createEmailAttachmentDraft,
  type EmailAttachmentDraft,
  type EmailAttachmentIssue,
  type UpdateEmailAttachmentDraft,
} from './email-attachment-draft';

interface Options {
  provider?: string;
  initialAttachments?: BatchAttachmentInfo[];
  draft?: EmailAttachmentDraft;
  updateDraft?: UpdateEmailAttachmentDraft;
}

const ERROR_PREFIX = 'bulkEmail.modal.attachments.errors.';
const ALLOWED_EXTENSIONS = /\.(pdf|jpe?g|png|gif|webp|docx|xlsx|pptx)$/i;

/** Shared composer lifecycle. Only uploads created by this draft may be deleted. */
export function useEmailAttachments({
  provider,
  initialAttachments,
  draft,
  updateDraft,
}: Options = {}) {
  const { t } = useTranslation();
  const [localDraft, setLocalDraft] = useState(() =>
    createEmailAttachmentDraft(initialAttachments)
  );
  const current = draft ?? localDraft;
  const update = updateDraft ?? setLocalDraft;
  const currentRef = useRef(current);
  currentRef.current = current;
  const upload = useUploadBulkEmailAttachment();
  const remove = useDeleteBulkEmailAttachment();

  const addIssue = useCallback(
    (sessionId: string, issue: EmailAttachmentIssue) => {
      update((previous) =>
        previous.sessionId !== sessionId
          ? previous
          : {
              ...previous,
              issues: [...previous.issues.filter((item) => item.id !== issue.id), issue],
            }
      );
      toaster.create({ title: t(issue.messageKey), description: issue.name, type: 'error' });
    },
    [t, update]
  );

  const errorKey = useCallback(
    (error: unknown, fallback: string) => {
      const code = (error as AxiosError<{ code?: string }>)?.response?.data?.code;
      return code && t(`errors.${code}`, { defaultValue: '' })
        ? `errors.${code}`
        : ERROR_PREFIX + fallback;
    },
    [t]
  );

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      const snapshot = currentRef.current;
      if (!files?.length || snapshot.uploading || snapshot.deletingId) return;
      const selected = Array.from(files);
      const invalid = attachmentSetError([...snapshot.attachments, ...selected], provider);
      const invalidType = selected.find((file) => !ALLOWED_EXTENSIONS.test(file.name));
      if (invalid || invalidType) {
        addIssue(snapshot.sessionId, {
          id: 'selection',
          name: invalidType?.name ?? selected.map((file) => file.name).join(', '),
          messageKey: ERROR_PREFIX + (invalid ?? 'invalidType'),
        });
        return;
      }
      // Set the ref synchronously too: a second picker event must not bypass count checks.
      currentRef.current = { ...snapshot, uploading: true };
      update((previous) => ({
        ...previous,
        uploading: true,
        issues: previous.issues.filter((issue) => issue.id !== 'selection'),
      }));
      try {
        for (const file of selected) {
          try {
            const result = await upload.mutateAsync({ file, sessionId: snapshot.sessionId });
            update((previous) =>
              previous.sessionId !== snapshot.sessionId
                ? previous
                : {
                    ...previous,
                    attachments: [
                      ...previous.attachments,
                      {
                        id: result.id,
                        name: result.name,
                        size: result.size,
                        mimeType: result.mimeType,
                      },
                    ],
                    temporaryIds: [...previous.temporaryIds, result.id],
                    issues: previous.issues.filter((issue) => issue.id !== file.name),
                    customized: true,
                  }
            );
          } catch (error) {
            addIssue(snapshot.sessionId, {
              id: file.name,
              name: file.name,
              messageKey: errorKey(error, 'uploadFailed'),
            });
          }
        }
      } finally {
        update((previous) =>
          previous.sessionId !== snapshot.sessionId ? previous : { ...previous, uploading: false }
        );
      }
    },
    [addIssue, errorKey, provider, update, upload]
  );

  const handleRemoveAttachment = useCallback(
    async (id: string) => {
      const snapshot = currentRef.current;
      if (snapshot.uploading || snapshot.deletingId) return;
      const discard = (previous: EmailAttachmentDraft): EmailAttachmentDraft =>
        previous.sessionId !== snapshot.sessionId
          ? previous
          : {
              ...previous,
              attachments: previous.attachments.filter((file) => file.id !== id),
              temporaryIds: previous.temporaryIds.filter((fileId) => fileId !== id),
              issues: previous.issues.filter((issue) => issue.attachmentId !== id),
              customized: true,
            };
      // A template reference belongs to the template; removing it from one message is local.
      if (!snapshot.temporaryIds.includes(id)) {
        update(discard);
        return;
      }
      currentRef.current = { ...snapshot, deletingId: id };
      update((previous) => ({ ...previous, deletingId: id }));
      try {
        await remove.mutateAsync(id);
        update(discard);
      } catch (error) {
        addIssue(snapshot.sessionId, {
          id,
          attachmentId: id,
          name: snapshot.attachments.find((file) => file.id === id)?.name ?? '',
          messageKey: errorKey(error, 'deleteFailed'),
        });
      } finally {
        update((previous) =>
          previous.sessionId !== snapshot.sessionId ? previous : { ...previous, deletingId: null }
        );
      }
    },
    [addIssue, errorKey, remove, update]
  );

  const setAttachments = useCallback(
    (value: SetStateAction<BatchAttachmentInfo[]>) => {
      update((previous) => ({
        ...previous,
        customized: true,
        attachments: typeof value === 'function' ? value(previous.attachments) : value,
      }));
    },
    [update]
  );
  const resetAttachments = useCallback(
    (files: BatchAttachmentInfo[] = []) => {
      update(() => createEmailAttachmentDraft(files));
    },
    [update]
  );
  const replaceAttachments = useCallback(
    (files: BatchAttachmentInfo[]): boolean | Promise<boolean> => {
      const snapshot = currentRef.current;
      if (snapshot.uploading || snapshot.deletingId) return false;
      const retainedIds = new Set(files.map((file) => file.id));
      const discardedIds = snapshot.temporaryIds.filter((id) => !retainedIds.has(id));
      if (!discardedIds.length) {
        resetAttachments(files);
        return true;
      }
      const cleanup = async () => {
        currentRef.current = { ...snapshot, deletingId: discardedIds[0] };
        update((previous) => ({ ...previous, deletingId: discardedIds[0] }));
        try {
          for (const id of discardedIds) {
            if (currentRef.current.sessionId !== snapshot.sessionId) return false;
            try {
              await remove.mutateAsync(id);
              update((previous) =>
                previous.sessionId !== snapshot.sessionId
                  ? previous
                  : {
                      ...previous,
                      attachments: previous.attachments.filter((file) => file.id !== id),
                      temporaryIds: previous.temporaryIds.filter((fileId) => fileId !== id),
                    }
              );
            } catch (error) {
              addIssue(snapshot.sessionId, {
                id,
                attachmentId: id,
                name: snapshot.attachments.find((file) => file.id === id)?.name ?? '',
                messageKey: errorKey(error, 'deleteFailed'),
              });
              return false;
            }
          }
          if (currentRef.current.sessionId !== snapshot.sessionId) return false;
          update((previous) =>
            previous.sessionId !== snapshot.sessionId ? previous : createEmailAttachmentDraft(files)
          );
          return true;
        } finally {
          update((previous) =>
            previous.sessionId !== snapshot.sessionId ? previous : { ...previous, deletingId: null }
          );
        }
      };
      return cleanup();
    },
    [addIssue, errorKey, remove, resetAttachments, update]
  );
  const dismissIssue = useCallback(
    (id: string) => {
      update((previous) => ({
        ...previous,
        issues: previous.issues.filter((issue) => issue.id !== id || issue.attachmentId),
      }));
    },
    [update]
  );
  const validationError = attachmentSetError(current.attachments, provider);

  return {
    attachments: current.attachments,
    setAttachments,
    replaceAttachments,
    resetAttachments,
    handleFileSelect,
    handleRemoveAttachment,
    dismissIssue,
    issues: current.issues,
    isUploading: current.uploading,
    deletingId: current.deletingId,
    customized: current.customized,
    maxTotalBytes: attachmentTotalLimit(provider),
    validationError: validationError ? ERROR_PREFIX + validationError : null,
    isBusy: current.uploading || current.deletingId !== null,
    isSaveBlocked:
      current.uploading ||
      current.deletingId !== null ||
      current.issues.length > 0 ||
      !!attachmentSetError(current.attachments),
    isBlocked:
      current.uploading ||
      current.deletingId !== null ||
      current.issues.length > 0 ||
      !!validationError,
  };
}
